/**
 * Whether the visitor has agreed to analytics.
 *
 * Kept apart from the banner, because the banner is not the only thing that
 * needs the answer: whatever loads PostHog has to ask before it loads anything,
 * and has to hear about it if the answer changes without a page reload. A
 * module with a subscription is the smallest thing that does both.
 *
 * The one rule this exists to enforce: nothing analytics-related may run until
 * `analyticsAllowed()` is true. Loading the script first and deciding later is
 * the thing a cookie banner is supposed to prevent, and it is an easy mistake
 * to make because the page looks identical either way.
 *
 * In core rather than in one of the apps because both need it, and they need it
 * separately. The marketing site and the portal are different origins — today
 * veyso-site.fly.dev and veyso-web.fly.dev, tomorrow veyso.io and app.veyso.io
 * — and localStorage does not cross an origin, subdomains included. So each
 * app asks its own visitor and stores its own answer; what is shared is this
 * logic, not the answer. A banner on the site that silently granted consent
 * for the app would be claiming something the browser never told it.
 *
 * Nothing here runs on the server. It is in core because core is what both
 * browsers already import, and every access is guarded.
 */
export type Consent = "accepted" | "declined";

const KEY = "veyso.consent";

const listeners = new Set<(value: Consent | null) => void>();

/**
 * Reading localStorage throws outright in some settings — a browser configured
 * to block site data, an embedded view — rather than returning nothing. An
 * analytics preference is not worth taking the page down for, so a failure here
 * reads as "not asked yet", which shows the banner again and grants nothing.
 */
export function consent(): Consent | null {
  try {
    const stored = window.localStorage.getItem(KEY);
    return stored === "accepted" || stored === "declined" ? stored : null;
  } catch {
    return null;
  }
}

export function analyticsAllowed(): boolean {
  return consent() === "accepted";
}

export function setConsent(value: Consent): void {
  try {
    window.localStorage.setItem(KEY, value);
  } catch {
    // Not fatal: the choice holds for this page view, and the banner asks again
    // next time rather than the button appearing to do nothing now.
  }
  for (const notify of listeners) notify(value);
}

/** Called when the choice is made, so analytics can start without a reload. */
export function onConsentChange(handler: (value: Consent | null) => void): () => void {
  listeners.add(handler);
  return () => listeners.delete(handler);
}
